import { motion } from "framer-motion";
import { SiInstagram, SiFacebook, SiFiverr, SiGmail } from "react-icons/si";
import { Mail, ExternalLink } from "lucide-react";

const socials = [
  {
    name: "Instagram",
    icon: <SiInstagram className="w-8 h-8" />,
    href: "https://www.instagram.com/xuvera122/",
    color: "hover:text-pink-500",
    glow: "hover:shadow-pink-500/20",
    isSpecial: false,
  },
  {
    name: "Facebook",
    icon: <SiFacebook className="w-8 h-8" />,
    href: "https://www.facebook.com/profile.php?id=61586920177576",
    color: "hover:text-blue-500",
    glow: "hover:shadow-blue-500/20",
    isSpecial: false,
  },
  {
    name: "Fiverr",
    icon: <SiFiverr className="w-12 h-12" />,
    href: "https://www.fiverr.com/xuvera1/buying?source=avatar_menu_profile",
    color: "hover:text-emerald-500",
    glow: "hover:shadow-emerald-500/20",
    isSpecial: false,
  },
  {
    name: "Gmail",
    icon: <SiGmail className="w-8 h-8" />,
    href: "https://mail.google.com/mail/u/0/#search/xuvera122%40gmail.com?compose=new",
    color: "text-destructive",
    glow: "shadow-[0_0_25px_rgba(var(--destructive),0.4)]",
    isSpecial: true,
  },
];

export function SocialConnect() {
  return (
    <section id="social-connect" className="py-24 relative overflow-hidden">
      {/* Ambient background glow */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] bg-primary/10 rounded-full blur-[120px] pointer-events-none" />

      <div className="container mx-auto px-4 relative z-10">
        <div className="text-center mb-16">
          <motion.h2 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-4xl md:text-5xl font-bold mb-4 bg-gradient-to-r from-foreground via-foreground/80 to-muted-foreground bg-clip-text text-transparent"
          >
            Connect With Xuvera Agency
          </motion.h2>
          <motion.p 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            viewport={{ once: true }}
            className="text-muted-foreground text-lg max-w-2xl mx-auto"
          >
            Join our digital ecosystem and let's start building your futuristic vision today.
          </motion.p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {socials.map((social, index) => (
            <motion.a
              key={social.name}
              href={social.href}
              target="_blank"
              rel="noopener noreferrer"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              viewport={{ once: true }}
              whileHover={{ scale: 1.02, y: -5 }}
              whileTap={{ scale: 0.98 }}
              className={`
                relative group flex flex-col items-center justify-center p-8 rounded-2xl transition-all duration-300
                border backdrop-blur-xl
                ${social.isSpecial 
                  ? "bg-destructive/5 border-destructive/30 hover:border-destructive shadow-lg shadow-destructive/10 hover:shadow-destructive/20" 
                  : "bg-card/40 border-white/10 hover:border-primary/50 hover:bg-card/60"
                }
              `}
            >
              {/* Hover Rim Light Effect */}
              <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-white/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
              
              <div className={`mb-4 transition-transform duration-300 group-hover:scale-110 ${social.color}`}>
                {social.icon}
              </div>

              <div className="flex flex-col items-center">
                <span className={`text-xl font-semibold mb-1 ${social.isSpecial ? "text-destructive" : "text-foreground"}`}>
                  {social.name}
                </span>
                <span className="text-xs text-muted-foreground uppercase tracking-widest flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-all transform translate-y-2 group-hover:translate-y-0">
                  {social.isSpecial ? <Mail className="w-3 h-3" /> : <ExternalLink className="w-3 h-3" />}
                  {social.isSpecial ? "Open Email App" : "Visit Profile"}
                </span>
              </div>

              {/* Glow accent for Gmail */}
              {social.isSpecial && (
                <div className="absolute -inset-0.5 bg-destructive/20 blur-xl opacity-50 group-hover:opacity-100 transition-opacity rounded-2xl -z-10" />
              )}
            </motion.a>
          ))}
        </div>

        {/* Minimal Bottom CTA */}
        <motion.div 
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          transition={{ delay: 0.5 }}
          viewport={{ once: true }}
          className="mt-16 text-center"
        >
          <p className="text-sm text-muted-foreground font-mono">
            SYSTEM STATUS: <span className="text-primary animate-pulse">ACTIVE</span> // READY FOR NEW DEPLOYMENTS
          </p>
        </motion.div>
      </div>
    </section>
  );
}
